import { useCallback, useState } from 'react';
import { FormikErrors, FormikHelpers, useFormik } from 'formik';
import { isNil, omitBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Button, DocumentUpload, Typography } from 'components';
import HazardIcon from 'assets/icons/hazard.svg?react';
import { useDocumentUpload } from 'hooks/webapi';
import {
  validateFileType,
  validateMaxSize,
  validateNonEmpty,
} from 'utils/validations';
import useEventCollector from 'hooks/context/EventCollector/useEventCollector';
import FileSelectEvent from 'hooks/context/EventCollector/events/FileSelectEvent';
import styles from './UploadID.module.scss';

type Props = {
  onSuccess: () => void;
  buttonText?: string;
};

type FormValues = {
  frontId: Blob | null;
  backId: Blob | null;
};

export default function UploadID({ buttonText, onSuccess }: Props) {
  const { t } = useTranslation(['manual_upload_id', 'common', 'error']);
  const { uploadAttachment, sendAttachmentsToOCR } = useDocumentUpload();
  const [isPending, setIsPending] = useState(false);
  const { track } = useEventCollector();

  const handleSubmit = useCallback(
    async (form: FormValues, formikBag: FormikHelpers<FormValues>) => {
      const formDataFront = new FormData();
      const formDataBack = new FormData();

      if (!form.frontId || !form.backId) {
        return;
      }

      formDataFront.append('type', 'idCard');
      formDataFront.append('file', form.frontId);

      formDataBack.append('type', 'idCard');
      formDataBack.append('file', form.backId);

      try {
        setIsPending(true);
        const frontUploadId = await uploadAttachment(formDataFront);
        const backUploadId = await uploadAttachment(formDataBack);

        const docsList = [
          {
            type: 'ID_FRONT',
            id: frontUploadId.id,
          },
          {
            type: 'ID_BACK',
            id: backUploadId.id,
          },
        ];

        await sendAttachmentsToOCR(docsList);

        if (onSuccess) {
          onSuccess();
        }
      } catch {
        formikBag.setStatus('error');
        formikBag.setValues({
          frontId: null,
          backId: null,
        });
      } finally {
        setIsPending(false);
      }
    },
    [uploadAttachment, sendAttachmentsToOCR, onSuccess],
  );

  const validate = useCallback(({ frontId, backId }: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    errors.frontId =
      validateNonEmpty(frontId) ??
      validateFileType(frontId) ??
      validateMaxSize(frontId);

    errors.backId =
      validateNonEmpty(backId) ??
      validateFileType(backId) ??
      validateMaxSize(backId);

    return omitBy(errors, isNil);
  }, []);

  const formik = useFormik<FormValues>({
    initialValues: {
      frontId: null,
      backId: null,
    },
    validate,
    onSubmit: handleSubmit,
  });

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { name } = event.target;
      const file = event.target.files ? event.target.files[0] : null;
      formik.setFieldTouched(name, true, true);
      formik.setFieldValue(name, file);
      track(
        new FileSelectEvent({
          description: `${name} file selected`,
        }),
      );
    },
    [formik, track],
  );

  const handleDelete = useCallback(
    (name: string) => {
      formik.setFieldValue(name, null);
    },
    [formik],
  );

  const submitBtnDisabled = !formik.values.frontId || !formik.values.backId;

  return (
    <form
      onSubmit={formik.handleSubmit}
      className={styles.form}
      data-test="uploadDocuments-uploadId"
    >
      <div className={styles.uploads}>
        <DocumentUpload
          name="frontId"
          className={styles.upload}
          type="ID_FRONT"
          onChange={handleChange}
          isTouched={formik.touched.frontId}
          error={formik.errors.frontId}
          value={formik.values.frontId}
          onDelete={handleDelete}
          dataTest="id-front"
        />

        <DocumentUpload
          name="backId"
          className={styles.upload}
          type="ID_BACK"
          onChange={handleChange}
          isTouched={formik.touched.backId}
          error={formik.errors.backId}
          value={formik.values.backId}
          onDelete={handleDelete}
          dataTest="id-back"
        />
      </div>

      {formik.status === 'error' && (
        <div
          className={styles['error-wrapper']}
          data-test="uploadDocuments-error-wrapper"
        >
          <HazardIcon
            className={styles['error-icon']}
            data-test="uploadDocuments-error-icon"
          />
          <Typography
            className={styles.error}
            dataTest="uploadDocuments-error-message"
          >
            {t('error:validation.invalid_image')}
          </Typography>
        </div>
      )}

      <Button
        pending={isPending}
        type="submit"
        className={styles.continue}
        disabled={submitBtnDisabled}
        data-test="uploadDocuments-submit"
      >
        {buttonText || t('common:continue')}
      </Button>
    </form>
  );
}
